import { useEffect, useCallback, useRef } from "react";
import { useThree } from "@react-three/fiber";
import { MathUtils, Vector3, type Box3 } from "three";

interface BoundsCache {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

export default function CameraBoundsController({
  controlsRef,
  bbox,
  padding = 0.2,
  maxDistanceMultiplier = 3.0,
  isCameraAnimating = false,
}: {
  controlsRef: React.MutableRefObject<any>;
  bbox: Box3 | null;
  padding?: number;
  maxDistanceMultiplier?: number;
  /** Флаг анимации камеры - пропускаем проверку bounds во время анимации */
  isCameraAnimating?: boolean;
}) {
  const { camera, invalidate } = useThree();
  const boundsRef = useRef<BoundsCache>({ minX: 0, maxX: 0, minZ: 0, maxZ: 0 });

  // Вычисляем bounds и настраиваем distance один раз при изменении bbox
  useEffect(() => {
    const controls = controlsRef.current;
    if (!controls || !bbox) return;

    // Кэшируем bounds
    boundsRef.current = {
      minX: bbox.min.x + padding,
      maxX: bbox.max.x - padding,
      minZ: bbox.min.z + padding,
      maxZ: bbox.max.z - padding,
    };

    // Настраиваем distance limits
    const size = bbox.getSize(new Vector3());
    const maxSide = Math.max(size.x, size.z, size.y, 1);

    controls.minDistance = 10;
    controls.maxDistance = maxSide * maxDistanceMultiplier;
    controls.zoomSpeed = 0.8;
  }, [bbox, controlsRef, padding, maxDistanceMultiplier]);

  // Функция проверки и коррекции bounds
  const checkBounds = useCallback(() => {
    // Пропускаем проверку во время анимации камеры (RouteCameraController сам учитывает bounds)
    if (isCameraAnimating) return;

    const controls = controlsRef.current;
    if (!controls || !bbox) return;

    const { minX, maxX, minZ, maxZ } = boundsRef.current;
    const prevTarget = controls.target.clone();

    const clampedTarget = prevTarget.clone();
    clampedTarget.x = MathUtils.clamp(clampedTarget.x, minX, maxX);
    clampedTarget.z = MathUtils.clamp(clampedTarget.z, minZ, maxZ);

    if (!clampedTarget.equals(prevTarget)) {
      const offset = camera.position.clone().sub(prevTarget);
      controls.target.copy(clampedTarget);
      camera.position.copy(clampedTarget.clone().add(offset));
      controls.update?.();
      invalidate(); // Запрашиваем перерисовку после коррекции
    }
  }, [controlsRef, bbox, camera, invalidate, isCameraAnimating]);

  // Подписываемся на изменения controls вместо useFrame
  useEffect(() => {
    const controls = controlsRef.current;
    if (!controls || !bbox) return;

    // Проверяем bounds при каждом изменении controls
    const handleChange = () => {
      checkBounds();
    };

    controls.addEventListener("change", handleChange);

    // Начальная проверка
    checkBounds();

    return () => {
      controls.removeEventListener("change", handleChange);
    };
  }, [controlsRef.current, bbox, checkBounds]);

  return null;
}
